/* ============================================================================
   Farmkart Partial-Payment — frontend controller
   ----------------------------------------------------------------------------
   Spec : docs/superpowers/specs/2026-05-25-partial-payment-build-spec.md
   Mock : docs/mocks/2026-05-25-partial-payment-mock.html
   Snippet: snippets/partial-pay-button.liquid
   Styles : assets/partial-pay.css

   FLOW:
     1. User clicks [data-fk-pp-open] → modal opens at state=form
     2. Form submit → POST /apps/partial-pay/initiate (mock for now)
     3. Pincode-loading → ShipRocket pincode check (reuses /apps/shipping)
     4. Summary → user reviews → clicks Pay → Razorpay Standard Checkout opens
     5. On Razorpay handler → POST /apps/partial-pay/result → poll for order
     6. Success → order confirmed, balance reminder shown
     7. Error path: any failure → state=error → retry routes back to summary

   MOCK MODE:
     Append ?fk_pp_mock=1 to URL → backend calls return fake data so the full
     UX is reviewable without Vercel endpoints. Use for design QA.

   ANALYTICS:
     Fires custom events (forward-compatible + legacy Breeze names for tracking pipeline reuse):
       'farmkart-partial-pay-initiate'  + legacy 'breeze-initiate-checkout'
       'farmkart-partial-pay-purchase'  + legacy 'breeze-purchase'
       'farmkart-partial-pay-error'

   ACCESSIBILITY:
     - role=dialog, aria-modal, focus trap, ESC to close, return focus on close.
     - hidden attribute toggled per state (not just display:none) for screen readers.
   ============================================================================ */

(function () {
  'use strict';

  // ────────────────────────────────────────────────────────────────────────
  // CONFIG & UTILITIES
  // ────────────────────────────────────────────────────────────────────────
  var CFG = window.FK_PARTIAL_PAY || {};
  var URL_PARAMS = new URLSearchParams(window.location.search);
  var MOCK_MODE = URL_PARAMS.get('fk_pp_mock') === '1' || CFG.debug === true;
  var RAZORPAY_SDK_URL = 'https://checkout.razorpay.com/v1/checkout.js';

  function log() {
    if (!CFG.debug && !MOCK_MODE) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[fk-pp]');
    console.log.apply(console, args);
  }

  function rupees(paise) {
    return CFG.currencySymbol + Math.round(paise / 100).toLocaleString('en-IN');
  }

  function ulid() {
    // Lightweight ULID-ish: 10-char timestamp + 16-char randomness
    var ts = Date.now().toString(36).toUpperCase().padStart(10, '0');
    var rand = '';
    for (var i = 0; i < 16; i++) {
      rand += 'ABCDEFGHJKMNPQRSTVWXYZ0123456789'.charAt(Math.floor(Math.random() * 32));
    }
    return ts + rand;
  }

  function dispatchEvent(name, data) {
    var detail = { data: data || {} };
    window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    // Legacy event names — keep existing breeze.js analytics passthrough alive
    if (name === 'farmkart-partial-pay-purchase') {
      window.dispatchEvent(new CustomEvent('breeze-purchase', { detail: detail }));
    }
    if (name === 'farmkart-partial-pay-initiate') {
      window.dispatchEvent(new CustomEvent('breeze-initiate-checkout', { detail: detail }));
    }
  }

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      // Razorpay SDK already on window — fast path.
      if (window.Razorpay) return resolve();

      var existing = document.querySelector('script[src="' + src + '"]');
      var s = existing || document.createElement('script');
      if (!existing) {
        s.src = src;
        s.async = true;
        document.head.appendChild(s);
      }

      // Poll for window.Razorpay because s.onload fires before some browsers
      // finish exposing the global, producing a false-positive
      // "Razorpay SDK failed to load" error even though the SDK then opens
      // milliseconds later (real bug witnessed on Tarun's 2026-05-26 payment).
      var deadline = Date.now() + 10000;
      var poll = setInterval(function () {
        if (window.Razorpay) {
          clearInterval(poll);
          resolve();
        } else if (Date.now() > deadline) {
          clearInterval(poll);
          reject(new Error('Razorpay SDK did not initialize within 10s'));
        }
      }, 50);

      s.onerror = function () {
        clearInterval(poll);
        reject(new Error('Failed to load ' + src));
      };
    });
  }

  // ────────────────────────────────────────────────────────────────────────
  // CART HELPERS — pulls live cart from Shopify Ajax API
  // ────────────────────────────────────────────────────────────────────────
  function fetchCart() {
    if (MOCK_MODE) {
      return Promise.resolve({
        total_price: 240000,
        items_subtotal_price: 235000,
        total_shipping_price: 5000,
        item_count: 3,
        token: 'mock_cart_token_' + Date.now(),
        items: [
          { id: 1, product_title: 'Roko Fungicide 250g', variant_title: null, quantity: 1, final_line_price: 62000, vendor: 'UPL', sku: 'FK-ROKO-250' },
          { id: 2, product_title: 'Bayer Insecticide 1L', variant_title: null, quantity: 1, final_line_price: 128000, vendor: 'Bayer', sku: 'FK-BAYER-1L' },
          { id: 3, product_title: 'Sticker Spreader 500ml', variant_title: null, quantity: 1, final_line_price: 45000, vendor: 'Adjuvant', sku: 'FK-STICKER-500' }
        ]
      });
    }
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      });
  }

  function computeDeposit(totalPaise, rate, floorPaise) {
    rate = rate || 0.05;
    floorPaise = floorPaise || 15000;
    var raw = Math.round(totalPaise * rate);
    return Math.max(raw, floorPaise);
  }

  // ────────────────────────────────────────────────────────────────────────
  // BACKEND ADAPTER — wraps fetch with mock-mode fallback
  // ────────────────────────────────────────────────────────────────────────
  function apiPost(path, body) {
    if (MOCK_MODE) {
      log('MOCK POST', path, body);
      return new Promise(function (resolve) {
        setTimeout(function () {
          resolve(mockResponse(path, body));
        }, 700 + Math.random() * 600);
      });
    }
    return fetch(CFG.apiBase + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body),
      credentials: 'same-origin'
    }).then(function (r) {
      return r.text().then(function (text) {
        var json = null;
        try { json = text ? JSON.parse(text) : {}; } catch (e) { json = { _raw: text }; }
        if (!r.ok) {
          var detail = (json && (json.detail || json.error)) || ('HTTP ' + r.status);
          var err = new Error('API ' + path + ' failed: ' + detail);
          err.status = r.status;
          err.body = json;
          throw err;
        }
        return json;
      });
    });
  }

  function mockResponse(path, body) {
    if (path === '/initiate') {
      var ppId = body.pp_id || ulid();
      return {
        ok: true,
        pp_id: ppId,
        razorpay_order_id: 'order_MOCK_' + ppId.slice(0, 10),
        razorpay_key_id: CFG.razorpayKeyId,
        amount_paise: body.deposit_paise,
        currency: 'INR'
      };
    }
    if (path === '/result' || path === '/order-confirm') {
      return {
        ok: true,
        pp_id: body.pp_id,
        order_name: '#FK-' + (4628 + Math.floor(Math.random() * 100)),
        order_id: 6900000000000 + Math.floor(Math.random() * 100000),
        financial_status: 'partially_paid',
        balance_paise: body.balance_paise || 0,
        estimated_delivery: '4–6 days'
      };
    }
    if (path === '/pincode-check') {
      var valid = /^\d{6}$/.test(body.pincode || '');
      return {
        ok: valid,
        serviceable: valid,
        city: body.city || 'Indore',
        edd_days: '4–6',
        shipping_paise: 5000,
        reason: valid ? null : 'invalid_pincode'
      };
    }
    return { ok: true };
  }

  // ────────────────────────────────────────────────────────────────────────
  // STATE MACHINE — drives modal visibility
  // ────────────────────────────────────────────────────────────────────────
  var STATES = ['form', 'pincode-loading', 'summary', 'razorpay', 'success', 'error', 'prepaid-only', 'cod-unconfirmed', 'not-deliverable'];
  var PROGRESS_INDEX = { 'form': 0, 'pincode-loading': 1, 'summary': 1, 'razorpay': 2, 'success': 3, 'error': 2, 'prepaid-only': 1, 'cod-unconfirmed': 1, 'not-deliverable': 1 };

  var ctx = {
    modal: null,
    isOpen: false,
    state: 'form',
    pp_id: null,
    triggerCTA: null,     // remembers which CTA opened the modal (for focus return)
    triggerContext: null, // 'pdp' | 'cart-drawer' | 'cart-page'
    totalPaise: 0,
    depositPaise: 0,
    balancePaise: 0,
    rate: 0.05,
    shopper: null,        // populated after form submission
    pinResult: null,      // populated after pincode check
    cart: null            // populated from Shopify /cart.js
  };

  function setState(state) {
    if (!ctx.modal) return;
    if (STATES.indexOf(state) === -1) {
      console.warn('[fk-pp] unknown state:', state);
      return;
    }
    ctx.state = state;
    log('state →', state);
    ctx.modal.querySelectorAll('[data-pp-state]').forEach(function (el) {
      var match = el.getAttribute('data-pp-state') === state;
      el.hidden = !match;
    });
    // Progress bar
    var idx = PROGRESS_INDEX[state];
    ctx.modal.querySelectorAll('.fk-pp-modal__progress span').forEach(function (el, i) {
      el.classList.remove('is-active', 'is-done');
      if (i < idx) el.classList.add('is-done');
      else if (i === idx) el.classList.add('is-active');
    });
    // Title update
    var titles = {
      'form': 'Reserve your order',
      'pincode-loading': 'Checking delivery…',
      'summary': 'Review & pay',
      'razorpay': 'Complete UPI payment',
      'success': 'All done!',
      'error': 'Payment issue',
      'prepaid-only': 'Prepaid only',
      'cod-unconfirmed': 'Couldn\'t confirm delivery',
      'not-deliverable': 'We don\'t deliver here yet'
    };
    var titleEl = ctx.modal.querySelector('#fk-pp-modal-title');
    if (titleEl && titles[state]) titleEl.textContent = titles[state];
    // Focus management — focus first focusable in newly-visible section
    setTimeout(function () {
      var section = ctx.modal.querySelector('[data-pp-state="' + state + '"]');
      if (!section) return;
      var firstField = section.querySelector('input, button, select, textarea, [href]');
      if (firstField) firstField.focus({ preventScroll: true });
    }, 50);
  }

  // ────────────────────────────────────────────────────────────────────────
  // MODAL OPEN / CLOSE
  // ────────────────────────────────────────────────────────────────────────
  function openModal(triggerBtn) {
    ctx.modal = document.getElementById('fk-pp-modal');
    if (!ctx.modal) {
      console.error('[fk-pp] Modal element #fk-pp-modal not found in DOM');
      return;
    }
    // Portal fix — run on every open, not just at init.
    // Cart AJAX re-renders inject a new #fk-pp-modal inside the drawer footer
    // (overflow:hidden). If we don't reparent before opening, the modal renders
    // invisibly behind the overflow boundary.
    if (ctx.modal.parentNode !== document.body) {
      document.body.appendChild(ctx.modal);
      log('modal reparented to document.body on open');
    }
    ctx.triggerCTA = triggerBtn;
    var ctaWrap = triggerBtn ? triggerBtn.closest('[data-fk-pp-cta]') : null;
    if (ctaWrap) {
      ctx.totalPaise = parseInt(ctaWrap.dataset.ppTotalPaise, 10) || 0;
      ctx.depositPaise = parseInt(ctaWrap.dataset.ppDepositPaise, 10) || 0;
      ctx.balancePaise = parseInt(ctaWrap.dataset.ppBalancePaise, 10) || 0;
      ctx.rate = parseFloat(ctaWrap.dataset.ppRate) || 0.05;
      ctx.triggerContext = ctaWrap.dataset.ppContext || 'unknown';
      // PDP context: build a single-line pseudo-cart from the displayed product.
      // Buy-Now flow — we reserve THIS product, not the cart contents.
      // Variant ID source order (most robust first):
      //   1. Snippet-set data-pp-variant-id (preferred; may be missing if section render cache is stale)
      //   2. Shopify product form's hidden id input (always present + fresh on every render)
      //   3. URL ?variant=NNN
      if (ctx.triggerContext === 'pdp') {
        var variantId = parseInt(ctaWrap.dataset.ppVariantId, 10);
        if (!variantId) {
          var hiddenInput = document.querySelector('product-form input[name="id"], form input.product-variant-id, form[action*="/cart/add"] input[name="id"]');
          if (hiddenInput && hiddenInput.value) variantId = parseInt(hiddenInput.value, 10);
        }
        if (!variantId) variantId = parseInt(URL_PARAMS.get('variant'), 10);
        var qtyInput = document.querySelector('input[name="quantity"], quantity-input input');
        var qty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
        if (variantId) {
          var titleEl = document.querySelector('h1.product__title, h1[class*="product__title"]');
          ctx.pdpItem = {
            id: variantId,
            product_title: ctaWrap.dataset.ppProductTitle || (titleEl ? titleEl.textContent.trim() : ''),
            quantity: qty,
            final_line_price: ctx.totalPaise * qty,
          };
          log('pdp variant resolved', { variant_id: variantId, qty: qty, source: ctaWrap.dataset.ppVariantId ? 'data-attr' : 'form-input-fallback' });
        } else {
          console.error('[fk-pp] PDP: could not determine variant_id from data-attr / form input / URL — will fail validation');
        }
      }
    }
    ctx.pp_id = ulid();

    // Always re-fetch the cart at modal open — cart may have changed since page load
    fetchCart().then(function (cart) {
      ctx.cart = cart;
      // For cart contexts, override totals with live cart (PDP uses the buy-now product total)
      if (ctx.triggerContext !== 'pdp') {
        ctx.totalPaise = cart.total_price;
        ctx.depositPaise = computeDeposit(ctx.totalPaise, ctx.rate);
        ctx.balancePaise = ctx.totalPaise - ctx.depositPaise;
      }
      log('opened', { context: ctx.triggerContext, total: ctx.totalPaise, deposit: ctx.depositPaise, balance: ctx.balancePaise });
      dispatchEvent('farmkart-partial-pay-initiate', {
        shopOrderId: null,
        totalPrice: ctx.totalPaise / 100,
        currency: 'INR',
        items: (cart.items || []).map(function (i) {
          return { id: i.id, name: i.product_title, price: i.final_line_price / 100, quantity: i.quantity, variant: i.variant_title };
        })
      });
    }).catch(function (err) {
      log('cart fetch failed', err);
      // Continue anyway with CTA-provided totals
    });

    ctx.modal.hidden = false;
    ctx.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    ctx.isOpen = true;
    // Pre-fill phone from any existing form data
    setState('form');
  }

  function closeModal() {
    if (!ctx.modal || !ctx.isOpen) return;
    ctx.modal.hidden = true;
    ctx.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    ctx.isOpen = false;
    if (ctx.triggerCTA) ctx.triggerCTA.focus({ preventScroll: true });
  }

  // ────────────────────────────────────────────────────────────────────────
  // ANALYTICS + FULL-PREPAID HELPERS
  // ────────────────────────────────────────────────────────────────────────
  function fkTrackCodFailure(pincode, reason) {
    try {
      if (window.gtag) window.gtag('event', 'cod_check_failed', { pincode: pincode, reason: reason });
    } catch (e) { /* analytics optional */ }
    try {
      if (window.clarity) window.clarity('event', 'cod_check_failed');
    } catch (e) { /* analytics optional */ }
    log('cod_check_failed', { pincode: pincode, reason: reason });
  }

  function goFullPrepaid() {
    // Cart contexts already hold the items; PDP (buy-now) must add the product first.
    if (ctx.triggerContext === 'pdp' && ctx.pdpItem && ctx.pdpItem.id) {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: ctx.pdpItem.id, quantity: ctx.pdpItem.quantity || 1 })
      }).then(function () { window.location.href = '/checkout'; })
        .catch(function () { window.location.href = '/cart'; });
    } else {
      window.location.href = '/checkout';
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // FORM SUBMIT → COD gate → pincode check → summary
  // ────────────────────────────────────────────────────────────────────────
  function handleFormSubmit(e) {
    e.preventDefault();
    var form = e.target;
    var data = {};
    new FormData(form).forEach(function (val, key) { data[key] = (val + '').trim(); });

    // Minimal client-side validation; backend should re-verify
    if (!data.phone || data.phone.length < 10) return alertInline('phone', 'Please enter a valid phone number');
    if (!data.name) return alertInline('name', 'Please enter your name');
    if (!data.address) return alertInline('address', 'Please enter your address');
    if (!data.city) return alertInline('city', 'Please enter your city');
    if (!/^\d{6}$/.test(data.pincode || '')) return alertInline('pincode', 'Please enter a valid 6-digit pincode');

    // Backend expects shopper.address to be a nested object (line1/city/state/pincode/country)
    ctx.shopper = {
      phone: data.phone,
      name: data.name,
      email: data.email || undefined,
      address: {
        line1: data.address,
        city: data.city,
        state: data.state || '',
        pincode: data.pincode,
        country: 'IN',
      },
    };
    ctx._lastPincode = data.pincode;
    setState('pincode-loading');

    var proxyBase = CFG.shippingApiBase || 'https://vercel-shipping.vercel.app/api';
    fetch(proxyBase + '/shipping?pincode=' + data.pincode + '&weight=0.5')
      .then(function (r) { return r.json(); })
      .then(function (s) {
        if (s && s.ok === true && s.cod_available === true) {
          proceedWithPincodeCheck(data);                 // COD ok → normal flow
        } else if (s && s.ok === true && s.cod_available === false) {
          setText(ctx.modal, '[data-pp-prepaid-pin]', data.pincode);
          setState('prepaid-only');                      // deliverable, no COD → pay full
        } else if (s && s.ok === false && s.error === 'not_serviceable') {
          setText(ctx.modal, '[data-pp-notdeliver-pin]', data.pincode);
          setState('not-deliverable');                   // legit result: outside delivery area (NOT a payment failure)
        } else {
          fkTrackCodFailure(data.pincode, (s && s.error) || 'malformed');
          setText(ctx.modal, '[data-pp-unconfirmed-pin]', data.pincode);
          setState('cod-unconfirmed');                   // check failed → human fallback
        }
      })
      .catch(function (err) {
        log('cod gate proxy error', err);
        fkTrackCodFailure(data.pincode, 'network');
        setText(ctx.modal, '[data-pp-unconfirmed-pin]', data.pincode);
        setState('cod-unconfirmed');
      });
  }

  function proceedWithPincodeCheck(data) {
    apiPost('/pincode-check', { pincode: data.pincode, city: data.city })
      .then(function (result) {
        ctx.pinResult = result;
        if (!result.serviceable) {
          setText(ctx.modal, '[data-pp-notdeliver-pin]', data.pincode);
          setState('not-deliverable');
          return;
        }
        applyShippingToTotals();
        renderSummary();
        setState('summary');
      })
      .catch(function (err) {
        log('pincode check failed', err);
        ctx.pinResult = { serviceable: true, city: data.city, edd_days: '4–7', shipping_paise: ctx.cart ? (ctx.cart.total_shipping_price || 5000) : 5000 };
        applyShippingToTotals();
        renderSummary();
        setState('summary');
      });
  }

  // Roll shipping into ctx.totalPaise + ctx.balancePaise once we know it from
  // the pincode check. PDP context starts with product-only total (Liquid snippet
  // can't know shipping until pincode is entered). Cart contexts already include
  // shipping via cart.total_price — skip there.
  //
  // Without this, the user saw "balance ₹428" on screen but Shopify charged
  // ₹478 on delivery (₹50 shipping mismatch). Real bug seen on order #4622.
  function applyShippingToTotals() {
    if (ctx.triggerContext !== 'pdp') return;     // cart contexts already include shipping
    if (ctx.shippingApplied) return;              // idempotent (back/edit pincode replays)
    var shippingPaise = (ctx.pinResult && ctx.pinResult.shipping_paise) || 0;
    if (shippingPaise <= 0) return;
    ctx.totalPaise += shippingPaise;
    ctx.balancePaise = Math.max(0, ctx.totalPaise - ctx.depositPaise);
    ctx.shippingApplied = true;
  }

  function alertInline(fieldName, msg) {
    var input = ctx.modal.querySelector('[name="' + fieldName + '"]');
    if (input) { input.focus(); input.setAttribute('aria-invalid', 'true'); }
    alert(msg);  // TODO Phase 4: replace with inline error UI per field
  }

  // ────────────────────────────────────────────────────────────────────────
  // SUMMARY RENDER
  // ────────────────────────────────────────────────────────────────────────
  function renderSummary() {
    var m = ctx.modal;
    // Pincode confirmation strip
    var cityEl = m.querySelector('[data-pp-pin-city]');
    var codeEl = m.querySelector('[data-pp-pin-code]');
    var eddEl = m.querySelector('[data-pp-pin-edd]');
    if (cityEl) cityEl.textContent = ctx.pinResult.city;
    if (codeEl) codeEl.textContent = (ctx.shopper.address && ctx.shopper.address.pincode) || '';
    if (eddEl) eddEl.textContent = ' · ' + (ctx.pinResult.edd_days || '4–7') + ' days · shipping ' + rupees(ctx.pinResult.shipping_paise || 0);

    // Line items
    var items = (ctx.cart && ctx.cart.items) || [];
    var itemsHtml = items.map(function (i) {
      return '<div class="fk-pp-summary__item"><span>' + escapeHtml(i.product_title) + ' × ' + i.quantity + '</span><span>' + rupees(i.final_line_price) + '</span></div>';
    }).join('');
    var itemsRoot = m.querySelector('[data-pp-summary-items]');
    if (itemsRoot) itemsRoot.innerHTML = itemsHtml || '<div class="fk-pp-summary__item"><span>Your cart</span><span>' + rupees(ctx.totalPaise - (ctx.pinResult.shipping_paise || 0)) + '</span></div>';

    // Totals
    var subtotalPaise = ctx.totalPaise - (ctx.pinResult.shipping_paise || 0);
    var shippingPaise = ctx.pinResult.shipping_paise || 0;
    setText(m, '[data-pp-summary-subtotal]', rupees(subtotalPaise));
    setText(m, '[data-pp-summary-shipping]', shippingPaise > 0 ? rupees(shippingPaise) : 'Free');
    setText(m, '[data-pp-summary-total]', rupees(ctx.totalPaise));

    // Deposit display
    setText(m, '[data-pp-deposit-amount]', rupees(ctx.depositPaise));
    setText(m, '[data-pp-balance-amount]', rupees(ctx.balancePaise));
    setText(m, '[data-pp-cta-amount]', rupees(ctx.depositPaise));
  }

  function setText(root, selector, value) {
    var el = root.querySelector(selector);
    if (el) el.textContent = value;
  }
  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"]/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]; });
  }

  // ────────────────────────────────────────────────────────────────────────
  // PAY (Razorpay)
  // ────────────────────────────────────────────────────────────────────────
  function handlePayClick() {
    // Re-entrancy guard: rapid double-clicks (or click during /initiate
    // round-trip) used to fire TWO parallel pipelines. The second one's
    // openRazorpay() saw `window.Razorpay` not yet loaded and called
    // showError, leaving the FK modal stuck in the error state behind the
    // Razorpay iframe that the first pipeline successfully opened. Single
    // flag short-circuits the second click.
    if (ctx.payInProgress) { log('pay click ignored — already in progress'); return; }
    ctx.payInProgress = true;

    setState('razorpay');
    // On PDP context, ALWAYS use just the displayed variant (Buy-Now style — doesn't touch cart).
    // On cart contexts, use the live cart contents.
    var sourceItems;
    if (ctx.triggerContext === 'pdp' && ctx.pdpItem) {
      sourceItems = [ctx.pdpItem];
    } else {
      sourceItems = (ctx.cart && ctx.cart.items) || [];
    }
    apiPost('/initiate', {
      pp_id: ctx.pp_id,
      context: ctx.triggerContext,
      total_paise: ctx.totalPaise,
      deposit_paise: ctx.depositPaise,
      balance_paise: ctx.balancePaise,
      shopper: ctx.shopper,
      pincode_result: ctx.pinResult,
      cart_token: (ctx.cart && ctx.cart.token) || '',
      items: sourceItems.map(function (i) {
        return { variant_id: i.id, quantity: i.quantity, price_paise: i.final_line_price };
      })
    })
      .then(function (init) {
        log('initiate ok', init);
        return loadScript(RAZORPAY_SDK_URL).then(function () { return init; });
      })
      .then(function (init) {
        openRazorpay(init);
      })
      .catch(function (err) {
        console.error('[fk-pp] initiate failed', err.status, err.body || err.message);
        var detail = (err.body && (err.body.detail || err.body.error)) || err.message || 'unknown';
        showError('Could not start payment. Reason: ' + detail);
      })
      .finally(function () {
        ctx.payInProgress = false;
      });
  }

  function openRazorpay(init) {
    if (MOCK_MODE) {
      log('MOCK Razorpay — simulating success in 1.8s');
      setTimeout(function () {
        handlePaymentResult({
          razorpay_payment_id: 'pay_MOCK_' + Date.now(),
          razorpay_order_id: init.razorpay_order_id,
          razorpay_signature: 'MOCK_SIG'
        });
      }, 1800);
      return;
    }

    if (!window.Razorpay) {
      showError('Razorpay SDK failed to load. Please refresh and try again.');
      return;
    }

    var rzp = new window.Razorpay({
      key: init.razorpay_key_id,
      amount: init.amount_paise,
      currency: 'INR',
      order_id: init.razorpay_order_id,
      name: CFG.merchantName || 'Farmkart',
      description: 'Deposit — balance ' + rupees(ctx.balancePaise) + ' on delivery',
      image: '/cdn/shop/files/farmkart-logo-square.png',  // TODO: replace with real square logo URL
      theme: { color: '#72BE43' },
      prefill: {
        name: ctx.shopper.name,
        email: ctx.shopper.email || '',
        contact: ctx.shopper.phone
      },
      method: { upi: true, card: false, netbanking: false, wallet: false },
      notes: { pp_id: ctx.pp_id, context: ctx.triggerContext },
      handler: function (response) {
        handlePaymentResult(response);
      },
      modal: {
        ondismiss: function () {
          log('Razorpay modal dismissed by user');
          setState('summary');  // bring them back to review screen
        }
      }
    });
    rzp.open();
  }

  function handlePaymentResult(response) {
    log('payment result', response);
    apiPost('/result', {
      pp_id: ctx.pp_id,
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_order_id: response.razorpay_order_id,
      razorpay_signature: response.razorpay_signature,
      balance_paise: ctx.balancePaise
    })
      .then(function (result) {
        log('order created', result);
        renderSuccess(result);
        setState('success');
        dispatchEvent('farmkart-partial-pay-purchase', {
          shopOrderId: result.order_name || result.order_id,
          totalPrice: ctx.totalPaise / 100,
          currency: 'INR',
          shippingCharge: (ctx.pinResult.shipping_paise || 0) / 100,
          items: (ctx.cart && ctx.cart.items || []).map(function (i) {
            return { id: i.id, name: i.product_title, price: i.final_line_price / 100, quantity: i.quantity, variant: i.variant_title };
          })
        });
      })
      .catch(function (err) {
        log('order create failed', err);
        showError('Payment received but order creation failed. Our team will contact you shortly. Your payment is safe.');
      });
  }

  function renderSuccess(result) {
    var m = ctx.modal;
    setText(m, '[data-pp-order-name]', result.order_name || ('#' + result.order_id));
    setText(m, '[data-pp-success-balance]', rupees(result.balance_paise || ctx.balancePaise) + ' due on delivery');
    setText(m, '[data-pp-success-edd]', result.estimated_delivery || (ctx.pinResult && ctx.pinResult.edd_days + ' days') || '4–7 days');
    var waLink = m.querySelector('[data-pp-whatsapp-link]');
    if (waLink && CFG.whatsAppNumber) {
      var waMsg = encodeURIComponent('Hi Farmkart! I just reserved order ' + (result.order_name || '#' + result.order_id) + '. Please send updates here. Thanks!');
      waLink.setAttribute('href', 'https://wa.me/' + CFG.whatsAppNumber + '?text=' + waMsg);
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR
  // ────────────────────────────────────────────────────────────────────────
  function showError(reason) {
    if (!ctx.modal) return;
    var reasonEl = ctx.modal.querySelector('[data-pp-error-reason]');
    if (reasonEl && reason) reasonEl.textContent = reason;
    setState('error');
    dispatchEvent('farmkart-partial-pay-error', { reason: reason, pp_id: ctx.pp_id });
  }

  // ────────────────────────────────────────────────────────────────────────
  // EVENT BINDINGS — delegated, work for dynamically-rendered CTAs too
  // ────────────────────────────────────────────────────────────────────────
  function onReady(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  onReady(function () {
    log('init', { mock: MOCK_MODE, cfg: CFG });

    // SAFETY BELT — two layers gate the CTA visibility for real shoppers.
    //   1. razorpayKeyId must not be a PLACEHOLDER (always required)
    //   2. EITHER the theme setting `partial_pay_enabled` is true (public launch)
    //      OR the URL has ?fk_pp_test=1 (private smoke testing by ops)
    // Mock mode (?fk_pp_mock=1) bypasses both for design QA.
    var keyOk = CFG.razorpayKeyId && CFG.razorpayKeyId.indexOf('PLACEHOLDER') === -1;
    var testModeUrl = URL_PARAMS.get('fk_pp_test') === '1';
    var publiclyEnabled = CFG.ctaEnabled === true;
    var shouldShow = MOCK_MODE || (keyOk && (publiclyEnabled || testModeUrl));

    if (!shouldShow) {
      var reason = !keyOk
        ? 'Razorpay key not configured'
        : 'partial-pay not publicly enabled — append ?fk_pp_test=1 to preview';
      console.warn('[fk-pp] hiding CTAs:', reason);
      document.querySelectorAll('.fk-pp-cta').forEach(function (el) { el.style.display = 'none'; });
      var modalToRemove = document.getElementById('fk-pp-modal');
      if (modalToRemove) modalToRemove.remove();
      return;
    }
    if (testModeUrl && !publiclyEnabled) {
      log('test mode (?fk_pp_test=1) — CTA visible to you only, hidden from real shoppers');
    }

    // PORTAL FIX — move the modal out of any wrapping form (e.g. the PDP product
    // form, cart-drawer form) onto document.body. HTML disallows nested <form>
    // elements; browsers silently strip the inner one, which deletes the modal's
    // submit button. Reparenting to body keeps the modal markup intact.
    var modal = document.getElementById('fk-pp-modal');
    if (modal && modal.parentNode !== document.body) {
      document.body.appendChild(modal);
      log('modal reparented to document.body');
    }

    document.addEventListener('click', function (e) {
      var openBtn = e.target.closest('[data-fk-pp-open]');
      if (openBtn) {
        e.preventDefault();
        openModal(openBtn);
        return;
      }
      var closeBtn = e.target.closest('[data-fk-pp-close]');
      if (closeBtn) {
        e.preventDefault();
        closeModal();
        return;
      }
      var payBtn = e.target.closest('[data-fk-pp-pay]');
      if (payBtn) {
        e.preventDefault();
        handlePayClick();
        return;
      }
      var backBtn = e.target.closest('[data-fk-pp-back-form]');
      if (backBtn) {
        e.preventDefault();
        setState('form');
        return;
      }
      var retryBtn = e.target.closest('[data-fk-pp-retry]');
      if (retryBtn) {
        e.preventDefault();
        setState('summary');
        return;
      }
    });

    document.addEventListener('click', function (e) {
      var t = e.target.closest ? e.target.closest('[data-fk-pp-pay-full],[data-fk-pp-pincode-retry],[data-fk-pp-wa-log]') : null;
      if (!t) return;
      if (t.hasAttribute('data-fk-pp-pay-full')) { e.preventDefault(); goFullPrepaid(); }
      else if (t.hasAttribute('data-fk-pp-pincode-retry')) { e.preventDefault(); setState('form'); }
      else if (t.hasAttribute('data-fk-pp-wa-log')) { fkTrackCodFailure(ctx._lastPincode, 'contact_click'); }
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest('[data-fk-pp-form]');
      if (form) handleFormSubmit(e);
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && ctx.isOpen) closeModal();
    });
  });
})();


/* cache-bust marker: 1779716966020847000 */
